<script>
    import Select from "svelte-select/Select.svelte";
    import DateInput from "../../../Components/Common/DateInput.svelte";

    import {
        InsuranceType,
        OOPType,
        BillingType,
        AustralianStates,
    } from "../../../Utils/utilEnums.svelte";

    import {
        insuranceFund,
        workersCompensation,
        trafficAccidentAuthorities,
        mbsToAsaDisallowInsuranceTypeByState,
        amaUnitValue,
    } from "../../../stores";

    import {
        checkMedicareNoValidation,
        getEmailValidation,
        setInputFilter,
        getAmountInCurrency,
        getDvaStringValidation,
        isObjectEmpty,
    } from "../../../Utils/utilFunctions.svelte";

    import {
        cacheInsuranceFunds,
        cacheWorkersCompensation,
        cacheTrafficAccidentAuthorities,
    } from "../../../Utils/utilCache.svelte";

    import {
        insuranceTypesAllowAsaItem,
        insuranceTypesMayAllowAmaBillingRule,
        insuranceTypesNotAllowBillingRule,
        noNeedForMedicareNoArr,
        unknownPatientInsuranceType,
    } from "../../../Utils/utilConstants.svelte";

    import {
        Loader,
        NotificationModal,
        DecimalInput,
    } from "../../../Components/Common/index.svelte";

    export let isModalOpen = false;
    export let latestObjDetails = {};
    export let latestQuoteObjDetails = {};
    export let patientEncounterDetails = {};
    export let isUpdatingBillingDetails = false;
    export let type = "";
    export let itemsData = [];
    export let closeModal = () => {};
    export let updateLatestDetails = () => {};

    $: if (latestObjDetails) {
        _syncBillingData();

        _syncInsuranceData();

        updateInputFields();
    }

    $: invalidDateRange =
        modalBillingData.startDate &&
        modalBillingData.endDate &&
        modalBillingData.startDate > modalBillingData.endDate;

    $: if (
        insuranceObj.insuranceType == 16 &&
        !$workersCompensation.length &&
        !isWorkersCompensationFetched
    )
        _loadWorkersCompensation();

    $: if (
        insuranceObj.insuranceType == 17 &&
        !$trafficAccidentAuthorities.length &&
        !isTrafficAuthoritiesFetched
    )
        _loadTrafficAuthorities();

    $: if (
        insuranceObj.insuranceType == 0 &&
        !$insuranceFund.length &&
        !isInsuranceFundFetched
    )
        _loadInsuranceFund();

    $: showCoverTypeForPrivateInsurance =
        insuranceObj.insuranceType == 0 &&
        insuranceObj.insuranceFund?.id &&
        (insuranceObj.insuranceFund.eclipseCode.toUpperCase() === "AMA" ||
            insuranceObj.insuranceFund.eclipseCode.toUpperCase() === "AUH");

    $: isAmaInsurance =
        showCoverTypeForPrivateInsurance &&
        insuranceObj.insuranceFund.eclipseCode.toUpperCase() === "AMA";

    $: isAuhInsurance =
        showCoverTypeForPrivateInsurance &&
        insuranceObj.insuranceFund.eclipseCode.toUpperCase() === "AUH";

    $: isValidMedicareNo =
        noNeedForMedicareNoArr.includes(Number(insuranceObj.insuranceType)) ||
        insuranceObj.insuranceType == unknownPatientInsuranceType
            ? true
            : checkMedicareNoValidation(insuranceObj.medicareNo);

    $: isValidInsurerEmail = insuranceObj.insurerEmail
        ? getEmailValidation(insuranceObj.insurerEmail)
        : true;

    $: isValidTrafficAuthorityEmail = insuranceObj.trafficAuthorityEmail
        ? getEmailValidation(insuranceObj.trafficAuthorityEmail)
        : true;

    $: isValidEmployerEmail = insuranceObj.workerEmployerEmail
        ? getEmailValidation(insuranceObj.workerEmployerEmail)
        : true;

    $: isValidPipEmail = insuranceObj.pipEmail
        ? getEmailValidation(insuranceObj.pipEmail)
        : true;

    $: hasItemTypeChanged =
        __DefaultItemUseASA__ != modalBillingData.useAsaCodes;

    // determine to show confirm action modal or not
    $: hasBillingTypeFiguresChanged =
        hasItemTypeChanged ||
        hasInsuranceTypeChange ||
        modalBillingData.billingType != latestObjDetails.billingType ||
        modalBillingData.calculationAmount !=
            latestObjDetails.calculationAmount;

    // when insurance type changes or for private insurance, change fund would reset latest billing details actual gap, rebate and total fee to 0
    $: hasInsuranceTypeChange =
        insuranceObj.insuranceType == 0 && !hasInsuranceTypeChange
            ? latestObjDetails.patientInsuranceId !=
              insuranceObj.insuranceFund?.id
            : latestObjDetails.insuranceType != insuranceObj.insuranceType;

    $: isInsuranceTypeEnableSwitchItemType =
        _checkInsuranceTypeEnableSwitchItemType(
            insuranceObj.insuranceType,
            modalBillingData,
            type,
        );

    $: displayTotalFee = _getModalBillingDataTotalFee(
        modalBillingData.addGST,
        modalBillingData.actualGap,
        modalBillingData.rebate,
        modalBillingData.totalFee,
    );

    $: isValidDvaNumber = insuranceObj.veteranNo
        ? getDvaStringValidation(insuranceObj.veteranNo)
        : insuranceObj.insuranceType != 20;

    $: if (insuranceObj.insuranceType == 15 || insuranceObj.insuranceType == 30)
        modalBillingData.addGST = Boolean(true);
    else modalBillingData.addGST = Boolean(false);

    $: insuranceAllowManuallyChangeBillingRule =
        (!insuranceTypesNotAllowBillingRule.includes(
            insuranceObj?.insuranceType,
        ) &&
            !insuranceTypesMayAllowAmaBillingRule.includes(
                insuranceObj.insuranceType,
            )) ||
        (insuranceTypesMayAllowAmaBillingRule.includes(
            insuranceObj.insuranceType,
        ) &&
            modalBillingData.usingAmaRates);

    $: isChangingBillingTypeDisabled =
        latestObjDetails.oopType === 1 &&
        isPastEncounter(patientEncounterDetails.scheduledDate);

    $: hasTimeItem = itemsData.some(({ infoType }) => infoType == "Time");

    $: isWcOrTacUsingAmaRates =
        insuranceTypesMayAllowAmaBillingRule.includes(
            insuranceObj.insuranceType,
        ) && latestObjDetails.usingAmaRates;

    $: displayBillingTypes = _getDisplayBillingTypes(
        hasTimeItem,
        isWcOrTacUsingAmaRates,
    );

    $: isInvoiceModalBillingTypeDisabled =
        type.toLowerCase() == "invoice" &&
        !isObjectEmpty(latestQuoteObjDetails);

    $: if (isInvoiceModalBillingTypeDisabled)
        modalBillingData.oopType = latestQuoteObjDetails.oopType;

    let modalBillingData = {};

    let insuranceObj = {};

    let isLoadingInsuranceFund = false,
        isInsuranceFundFetched = false,
        isLoadingWorkersCompensation = false,
        isWorkersCompensationFetched = false,
        isLoadingTrafficAuthorities = false,
        isTrafficAuthoritiesFetched = false;

    let changeItemTypeModalOpen = false;

    let __DefaultItemUseASA__, __DefaultInsuranceType__;

    let isShowInsuranceItemConflictModal = false;

    const updateProcess = () => {
        if (!modalBillingData.startDate) modalBillingData.startDate = null;
        if (!modalBillingData.endDate) modalBillingData.endDate = null;

        if (!_checkInputFieldsValid()) _updateInvalidFieldsStyle();
        else
            updateLatestDetails(
                hasBillingTypeFiguresChanged,
                modalBillingData,
                insuranceObj,
                hasItemTypeChanged,
                hasInsuranceTypeChange,
            );
    };

    const updateInputFields = () => {
        setInputFilter(
            document.getElementById("medicareNoField"),
            function (value) {
                return value ? /^[0-9-\s]+$/i.test(value) : true;
            },
            "Must be an integer",
        );

        setInputFilter(
            document.getElementById("medicareRefNoField"),
            function (value) {
                return value ? /^[0-9]+$/.test(value) : true;
            },
            "Must be an integer",
        );

        setInputFilter(
            document.getElementById("membershipNoInput"),
            function (value) {
                return value ? /^[A-z0-9]+$/.test(value) : true;
            },
            "Must be either an integer or a character",
        );

        setInputFilter(
            document.getElementById("membershipRefNoInput"),
            function (value) {
                return value ? /^[0-9]+$/.test(value) : true;
            },
            "Must be an integer",
        );
    };

    const handleItemsTypeChange = () => {
        if (insuranceObj.insuranceType !== undefined)
            __DefaultInsuranceType__ = insuranceObj.insuranceType;

        if (modalBillingData.id && !hasItemTypeChanged)
            openChangeItemTypeModal();
    };

    const changeItemType = () => {
        closeChangeItemTypeModal(true);
    };

    const openChangeItemTypeModal = () => (changeItemTypeModalOpen = true);

    const closeChangeItemTypeModal = (isChanged = false) => {
        changeItemTypeModalOpen = false;

        if (!isChanged)
            modalBillingData.useAsaCodes = !modalBillingData.useAsaCodes;
    };

    const openInsuranceItemConflictModal = () =>
        (isShowInsuranceItemConflictModal = true);

    const closeInsuranceItemConflictModal = (isChanged = false) => {
        isShowInsuranceItemConflictModal = false;

        if (isChanged) {
            __DefaultInsuranceType__ = insuranceObj.insuranceType;
            modalBillingData.useAsaCodes = false;
        } else if (insuranceObj.insuranceType != __DefaultInsuranceType__) {
            insuranceObj.insuranceType = __DefaultInsuranceType__;
            modalBillingData.useAsaCodes = true;
            // if (
            //     type.toString().toLowerCase != "quote" &&
            //     insuranceObj.workerCompensationObj?.id
            // )
            //     _clearWorkerCompensation();
            if (type.toString().toLowerCase() != "quote") {
                if (modalBillingData.insuranceType == 16)
                    _clearWorkerCompensation();
                else if (modalBillingData.insuranceType == 17)
                    _clearTrafficAuthority();
            }
        }
    };

    const handleChangeBillingType = () => {
        if (modalBillingData?.billingType == 1)
            modalBillingData.calculationAmount = 0;

        if (!insuranceAllowManuallyChangeBillingRule) {
            modalBillingData.calculationAmount = 0;
            modalBillingData.billingType = 3;
        }
    };

    // HELPER FUNCTIONS
    const _syncBillingData = () => {
        modalBillingData = {
            ...latestObjDetails,
            skipQuote: patientEncounterDetails.skipQuote,
        };

        if (!modalBillingData.id) {
            const { patient, skipQuote } = patientEncounterDetails;

            modalBillingData = {
                patientPaysTotalFee: Boolean(true),
                oopType: Number(0),
                isLatestBillingData: Boolean(true),
                skipQuote: skipQuote,
                addGST: Boolean(false),
                insuranceType: Number(patient.insuranceType),
                patientMedicareNo: patient.medicareNo,
                patientMedicareRefNo: patient.medicareRefNo,
                patientMembershipNo: patient.membershipNo,
                patientMembershipRefNo: patient.membershipRefNo,
                patientInsuranceId: patient.insuranceId,
                patientInsurance: patient.insurance,
                patientVeteranNo: patient.veteranNo,
                patientInsuranceState: patient.insuranceState,
            };
        }

        __DefaultItemUseASA__ =
            modalBillingData.useAsaCodes == undefined
                ? false
                : modalBillingData.useAsaCodes;
        __DefaultInsuranceType__ = modalBillingData.insuranceType;
    };

    const _syncInsuranceData = async () => {
        if (
            modalBillingData.insuranceType == 1 ||
            modalBillingData.insuranceType == 2
        )
            insuranceObj.insuranceType = 0;
        else insuranceObj.insuranceType = modalBillingData.insuranceType;

        insuranceObj.medicareNo = modalBillingData.patientMedicareNo ?? "";

        insuranceObj.medicareRefNo =
            modalBillingData.patientMedicareRefNo ?? "";

        insuranceObj.membershipNo = modalBillingData.patientMembershipNo ?? "";

        insuranceObj.membershipRefNo =
            modalBillingData.patientMembershipRefNo ?? "";

        switch (insuranceObj.insuranceType) {
            case 0: // Private
                insuranceObj.insuranceFundId =
                    modalBillingData.patientInsuranceId ?? Number(0);
                insuranceObj.privateCoverType =
                    modalBillingData.patientInsurance ?? "";
                await _loadInsuranceFund();
                break;
            case 10: // Overseas
                insuranceObj.overseasCover =
                    modalBillingData.patientInsurance ?? "";
                insuranceObj.insuranceFundId =
                    modalBillingData.patientInsuranceId ?? Number(0);
                
                break;
            case 16: // Workers Compensation
                const workerTempArr = modalBillingData.patientInsurance
                    .replaceAll(" ", "")
                    .split("|");

                if (workerTempArr.length == 4) {
                    insuranceObj.workerEmployer = workerTempArr[2];
                    insuranceObj.workerEmployerEmail = workerTempArr[3];
                    insuranceObj.workerInsurerEmail = "";
                } else if (workerTempArr.length == 3) {
                    insuranceObj.workerEmployer = workerTempArr[0];
                    insuranceObj.workerEmployerEmail = workerTempArr[1];
                    insuranceObj.workerClaimNumber = workerTempArr[2];
                }

                await _loadWorkersCompensation();
                break;
            case 17: // Traffic Accident
                insuranceObj.trafficClaimNumber =
                    modalBillingData.patientInsurance;

                await _loadTrafficAuthorities();
                break;
            case 20: // DVA
                insuranceObj.veteranNo = modalBillingData.patientVeteranNo;
                break;
            case 25: // Defence (BUPA)
            case 26: // Defence (AMA)
                const defenceTempArr = modalBillingData.patientInsurance
                    .replaceAll(" ", "")
                    .split("|");

                insuranceObj.pidNumber = defenceTempArr[0];
                insuranceObj.danNumber = defenceTempArr[1];
                break;
            case 30: // Public in Private
                insuranceObj.publicInPrivateObj = {
                    ...patientEncounterDetails.patientEncounterPIPData,
                };

                if (insuranceObj.publicInPrivateObj?.email)
                    insuranceObj.pipEmail =
                        insuranceObj.publicInPrivateObj.email;

                if (insuranceObj.publicInPrivateObj?.vendorNumbers)
                    insuranceObj.vendorNumbers =
                        insuranceObj.publicInPrivateObj.vendorNumbers;

                break;
            case 11: // Self insured
            case 15: // Cosmetic
            default:
                break;
        }
    };

    const _loadInsuranceFund = async () => {
        if ($insuranceFund.length)
            insuranceObj.insuranceFund = $insuranceFund.find(
                (fund) => fund.id == modalBillingData.patientInsuranceId,
            );
        else if (!isInsuranceFundFetched) {
            isInsuranceFundFetched = true;
            isLoadingInsuranceFund = true;

            await cacheInsuranceFunds();

            _loadInsuranceFund();

            isLoadingInsuranceFund = false;
        }
    };

    const _loadWorkersCompensation = async () => {
        if (
            $workersCompensation.length &&
            !insuranceObj.workerCompensationObj?.id &&
            patientEncounterDetails.patientEncounterWorkCoverData
                ?.workCoverAuthority
        ) {
            insuranceObj.workerCompensationObj = $workersCompensation.find(
                (compensation) =>
                    compensation.id ==
                    patientEncounterDetails.patientEncounterWorkCoverData
                        .workCoverAuthority,
            );

            if (insuranceObj.workerCompensationObj?.id)
                insuranceObj.insurerEmail =
                    patientEncounterDetails.patientEncounterWorkCoverData.email;
        } else if (!isWorkersCompensationFetched) {
            isWorkersCompensationFetched = true;
            isLoadingWorkersCompensation = true;

            await cacheWorkersCompensation();

            if (
                !insuranceObj.workerCompensationObj?.id &&
                patientEncounterDetails.patientEncounterWorkCoverData
                    ?.workCoverAuthority
            )
                insuranceObj.workerCompensationObj = $workersCompensation.find(
                    (compensation) =>
                        compensation.id ==
                        patientEncounterDetails.patientEncounterWorkCoverData
                            .workCoverAuthority,
                );

            if (insuranceObj.workerCompensationObj?.id)
                insuranceObj.insurerEmail =
                    patientEncounterDetails.patientEncounterWorkCoverData.email;

            isLoadingWorkersCompensation = false;
        }

        if (
            insuranceObj.workerCompensationObj?.id &&
            modalBillingData.useAsaCodes == undefined
        )
            modalBillingData.useAsaCodes =
                insuranceObj.workerCompensationObj.amaRates;
    };

    const _loadTrafficAuthorities = async () => {
        if (
            $trafficAccidentAuthorities.length &&
            !insuranceObj.trafficAuthorityObj?.id &&
            patientEncounterDetails.patientEncounterTrafficAccidentData
                ?.trafficAccidentAuthority
        ) {
            insuranceObj.trafficAuthorityObj = $trafficAccidentAuthorities.find(
                (authority) =>
                    authority.id ==
                    patientEncounterDetails.patientEncounterTrafficAccidentData
                        .trafficAccidentAuthority,
            );

            if (insuranceObj.trafficAuthorityObj?.id)
                insuranceObj.trafficAuthorityEmail =
                    patientEncounterDetails.patientEncounterTrafficAccidentData.email;
        } else if (!isTrafficAuthoritiesFetched) {
            isTrafficAuthoritiesFetched = true;
            isLoadingTrafficAuthorities = true;

            await cacheTrafficAccidentAuthorities();

            if (
                !insuranceObj.trafficAuthorityObj?.id &&
                patientEncounterDetails.patientEncounterTrafficAccidentData
                    ?.trafficAccidentAuthority
            )
                insuranceObj.trafficAuthorityObj =
                    $trafficAccidentAuthorities.find(
                        (authority) =>
                            authority.id ==
                            patientEncounterDetails
                                .patientEncounterTrafficAccidentData
                                .trafficAccidentAuthority,
                    );

            if (insuranceObj.trafficAuthorityObj?.id)
                insuranceObj.trafficAuthorityEmail =
                    patientEncounterDetails.patientEncounterTrafficAccidentData.email;

            isLoadingTrafficAuthorities = false;
        }
    };

    const _checkInputFieldsValid = () => {
        if (!isValidMedicareNo) return false;

        if (insuranceObj.insuranceType == unknownPatientInsuranceType)
            return true;

        if (insuranceObj.insuranceType == 0)
            return insuranceObj.insuranceFund?.id && insuranceObj.membershipNo;
        else if (
            insuranceObj.insuranceType == 10 ||
            insuranceObj.insuranceType == 11 ||
            insuranceObj.insuranceType == 15
        )
            return true;
        else if (insuranceObj.insuranceType == 16)
            return (
                (insuranceObj.workerCompensationObj?.id &&
                    insuranceObj.workerClaimNumber &&
                    isValidInsurerEmail) ||
                (insuranceObj.workerEmployer && isValidEmployerEmail)
            );
        else if (insuranceObj.insuranceType == 17)
            // return insuranceObj.trafficClaimNumber;
            return (
                insuranceObj.trafficAuthorityObj?.id &&
                insuranceObj.trafficClaimNumber &&
                isValidTrafficAuthorityEmail
            );
        else if (insuranceObj.insuranceType == 20) return isValidDvaNumber;
        else if (
            insuranceObj.insuranceType == 25 ||
            insuranceObj.insuranceType == 26
        )
            return insuranceObj.pidNumber && insuranceObj.danNumber;
        else if (insuranceObj.insuranceType == 30) return isValidPipEmail;

        return false;
    };

    const _updateInvalidFieldsStyle = () => {
        if (!isValidMedicareNo) _setErrorStyle("medicareNoField");

        if (insuranceObj.insuranceType == 0) {
            if (!insuranceObj.insuranceFund?.id)
                _setErrorStyle("insuranceFundContainer");
            if (!insuranceObj.membershipNo) _setErrorStyle("membershipNoInput");
        } else if (insuranceObj.insuranceType == 16)
            _checkWorkerCompensationValue();
        else if (insuranceObj.insuranceType == 17)
            _checkTrafficAuthorityValue();
        else if (insuranceObj.insuranceType == 20) {
            if (!isValidDvaNumber) _setErrorStyle("insuranceDetailsDVAField");
        } else if (
            insuranceObj.insuranceType == 25 ||
            insuranceObj.insuranceType == 26
        ) {
            if (!insuranceObj.pidNumber)
                _setErrorStyle("insuranceDetailsPIDField");
            if (!insuranceObj.danNumber)
                _setErrorStyle("insuranceDetailsDANField");
        } else if (insuranceObj.insuranceType == 30) {
            if (insuranceObj.pipEmail && !isValidPipEmail)
                _setErrorStyle("pipEmailInput");
        }
    };

    const _checkWorkerCompensationValue = () => {
        const pairOneMissing =
            !insuranceObj.workerCompensationObj?.id ||
            (insuranceObj.workerCompensationObj.id &&
                !insuranceObj.workerClaimNumber) ||
            !isValidInsurerEmail;

        const pairTwoMissing =
            !insuranceObj.workerEmployer ||
            !insuranceObj.workerEmployerEmail ||
            (insuranceObj.workerEmployerEmail && !isValidEmployerEmail);

        if (
            (pairOneMissing && !pairTwoMissing) ||
            (!pairOneMissing && pairTwoMissing)
        ) {
            _resetErrorStyle("workerCompensationContainer");
            _resetErrorStyle("workerCompensationInsurerEmailInput");
            _resetErrorStyle("workerCompensationClaimNumberInput");
            _resetErrorStyle("workerCompensationEmployerInput");
            _resetErrorStyle("workerCompensationEmployerEmailInput");
        } else if (pairOneMissing && pairTwoMissing) {
            _setErrorStyle("workerCompensationContainer");
            _setErrorStyle("workerCompensationClaimNumberInput");
            _setErrorStyle("workerCompensationEmployerInput");
            _setErrorStyle("workerCompensationEmployerEmailInput");
        }
    };

    const _checkTrafficAuthorityValue = () => {
        if (!insuranceObj.trafficAuthorityObj?.id) {
            const trafficAuthoritiesContainer = document.getElementById(
                "trafficAuthoritiesContainer",
            );
            trafficAuthoritiesContainer.style.outline = "red solid 2px";
            trafficAuthoritiesContainer.style.borderRadius = "5px";
        }

        if (!insuranceObj.trafficClaimNumber)
            _setErrorStyle("trafficClaimNoField");
    };

    const _getInsuranceFundLabel = (option) => option.name;

    const _clearInsuranceFund = () => {
        insuranceObj.insuranceFund = null;

        _resetErrorStyle("insuranceFundContainer");
    };

    const _handleSelectInsuranceFund = () => {
        insuranceObj.privateCoverType = isAmaInsurance
            ? "Normal Cover"
            : isAuhInsurance
              ? "Basic Plus"
              : "";
    };

    const _getWorkerCompensationLabel = (option) => option.name;

    const _clearWorkerCompensation = () => {
        insuranceObj.workerCompensationObj = null;

        _resetErrorStyle("workerCompensationContainer");
    };

    const _updateWorkcoverInsurerEmail = () =>
        (insuranceObj.insurerEmail = insuranceObj.workerCompensationObj.email);

    const _getTrafficAuthorityLabel = (option) => option.name;

    const _clearTrafficAuthority = () => {
        insuranceObj.trafficAuthorityObj = null;

        _resetErrorStyle("trafficAuthoritiesContainer");
    };

    const _updateTrafficAuthorityEmail = () => {
        insuranceObj.trafficAuthorityEmail =
            insuranceObj.trafficAuthorityObj.email;
    };

    const _setErrorStyle = (fieldId = "") => {
        const field = document.getElementById(`${fieldId}`);

        if (field) field.style.outline = "red 2px solid";
    };

    const _resetErrorStyle = (fieldId = "") => {
        const field = document.getElementById(`${fieldId}`);

        field.style.outline = "none";
    };

    const _checkInsuranceTypeEnableSwitchItemType = (
        insuranceType,
        modalData,
        billingDataType,
    ) => {
        return true;

        // const isQuote = billingDataType.toString().toLowerCase() == "quote";
        // const isSelectASACode = modalData.useAsaCodes;

        // if (isQuote) {
        //     if (
        //         insuranceType === undefined ||
        //         insuranceTypesAllowAsaItem.includes(Number(insuranceType))
        //     )
        //         return true;

        //     if (
        //         !insuranceTypesAllowAsaItem.includes(Number(insuranceType)) &&
        //         isSelectASACode
        //     )
        //         openInsuranceItemConflictModal();
        //     else return false;
        // } else {
        //     if (
        //         insuranceType === undefined ||
        //         insuranceTypesAllowAsaItem.includes(Number(insuranceType))
        //     )
        //         return true;

        //     if (insuranceType == 16 || insuranceType == 17) {
        //         const { patientInsuranceState } = modalData;
        //         const defaultStates = ["VIC", "QLD", "SA"];

        //         const notAllowAsaStatesArr =
        //             insuranceType == 16
        //                 ? [
        //                       ...$mbsToAsaDisallowInsuranceTypeByState[
        //                           "WorkCover"
        //                       ],
        //                   ] ?? defaultStates
        //                 : [
        //                       ...$mbsToAsaDisallowInsuranceTypeByState[
        //                           "TrafficAccident"
        //                       ],
        //                   ] ?? defaultStates;

        //         if (
        //             notAllowAsaStatesArr.includes(patientInsuranceState) &&
        //             isSelectASACode
        //         )
        //             modalBillingData.useAsaCodes = false;
        //         else
        //             return !notAllowAsaStatesArr.includes(
        //                 patientInsuranceState,
        //             );
        //     }

        //     return false;
        // }
    };

    // const _getItemTypeTooltipText = () => {
    //     if (type.toString().toLowerCase() == "quote")
    //         return "Only Overseas and Cosmetic insurances support ASA items";
    //     else if (
    //         Number(insuranceObj.insuranceType) == 16 ||
    //         Number(insuranceObj.insuranceType) == 17
    //     )
    //         return "State VIC, QLD and SA not support ASA items";
    //     else return "Only Overseas and Cosmetic insurances support ASA items";
    // };

    const _getModalBillingDataTotalFee = (
        addGST,
        actualGap,
        rebate,
        totalFee,
    ) => {
        if (!actualGap) actualGap = Number(0);
        if (!rebate) rebate = Number(0);

        if (totalFee) {
            const displayTotalFee =
                Number(totalFee) * (addGST ? Number(1.1) : Number(1));

            return getAmountInCurrency(displayTotalFee);
        }

        // modalBillingData.totalFee = Number(
        //     (actualGap + rebate) * (addGST ? Number(1.1) : Number(1))
        // );
        modalBillingData.totalFee = Number(actualGap + rebate);

        const displayTotalFee = Number(
            (actualGap + rebate) * (addGST ? Number(1.1) : Number(1)),
        );

        return getAmountInCurrency(displayTotalFee);
    };

    const _getDisplayBillingTypes = (
        timeItemExists = false,
        isWcOrTacUsingAmaRates = false,
    ) => {
        if (isWcOrTacUsingAmaRates) {
            const amaRatesBillingRule = {
                id: 2,
                type: "AMA Rates",
            };
            modalBillingData.billingType = amaRatesBillingRule.id;
            modalBillingData.calculationAmount =
                modalBillingData.calculationAmount
                    ? modalBillingData.calculationAmount
                    : $amaUnitValue;
            return [{ ...amaRatesBillingRule }];
        } else {
            const temp = BillingType.filter(
                (type) => type.id != 0 && type.id != 1 && type.id != 6,
            );

            return timeItemExists
                ? [...temp]
                : temp.filter((type) => type.id != 4);
        }
    };

    const isPastEncounter = (scheduledDate) => {
        if (!scheduledDate) return true;

        const scheduledTime = new Date(scheduledDate).getTime();
        const today = new Date();
        // today.setHours(0, 0, 0);
        return scheduledTime < today.getTime();
    };
</script>

<NotificationModal
    {isModalOpen}
    title={`${modalBillingData.id ? "Edit" : "Create"} ${type} Billing Data`}
    cancelText="Cancel"
    modalMaxWidth="max-w-6xl"
    showConfirmBtn={false}
    closeModalFunction={closeModal}
    {closeModal}
    isConfirming={isUpdatingBillingDetails}
    noContentPadding
    btnOptions={[
        {
            text: `${
                modalBillingData.id
                    ? isUpdatingBillingDetails
                        ? "Updating"
                        : "Update"
                    : isUpdatingBillingDetails
                      ? "Creating"
                      : "Create"
            }`,
            loading: isUpdatingBillingDetails || invalidDateRange,
            function: () => updateProcess(),
        },
    ]}
>
    <div class="flex flex-col flex-1 overflow-y-auto p-6">
        <div class="flex flex-1 gap-x-4">
            <div class="flex flex-1 justify-start flex-col gap-y-4">
                {#if !modalBillingData.id}
                    <div class="flex gap-x-4">
                        <p class="w-48">OOP Type:</p>
                        <select
                            class="input input-bordered input-sm w-60"
                            disabled={isUpdatingBillingDetails ||
                                isChangingBillingTypeDisabled ||
                                isInvoiceModalBillingTypeDisabled}
                            bind:value={modalBillingData.oopType}
                        >
                            {#each OOPType as oop}
                                <option value={oop.id}>
                                    {oop.type}
                                </option>
                            {/each}
                        </select>
                    </div>

                    <div class="flex gap-x-4">
                        <p class="w-48">Patient Pays Total Fee:</p>
                        <select
                            class="input input-bordered input-sm w-60"
                            disabled={isUpdatingBillingDetails}
                            bind:value={modalBillingData.patientPaysTotalFee}
                        >
                            <option value={Boolean(true)}> YES </option>
                            <option value={Boolean(false)}>NO</option>
                        </select>
                    </div>
                {/if}

                <div class="flex gap-x-4">
                    {#if isInsuranceTypeEnableSwitchItemType}
                        <div class="flex w-48 gap-x-1 h-8 items-center">
                            <p>Items type:</p>
                            <!-- {#if !isInsuranceTypeEnableSwitchItemType}
                                <button
                                    class="btn btn-ghost btn-circle tooltip tooltip-secondary tooltip-right"
                                    data-tip={_getItemTypeTooltipText()}
                                >
                                    <i class="fa-solid fa-circle-question" />
                                </button>
                            {/if} -->
                        </div>
                        <div class="flex flex-1 items-center gap-x-4">
                            <div class="flex items-center gap-x-2">
                                <input
                                    type="radio"
                                    name="radio-10"
                                    class="radio checked:bg-secondary"
                                    checked={!modalBillingData.useAsaCodes}
                                    on:change={() => {
                                        modalBillingData.useAsaCodes = false;
                                        handleItemsTypeChange();
                                    }}
                                />
                                <p>MBS</p>
                            </div>

                            <div class="flex items-center gap-x-2">
                                <input
                                    type="radio"
                                    name="radio-10"
                                    class="radio checked:bg-secondary"
                                    checked={modalBillingData.useAsaCodes}
                                    on:change={() => {
                                        modalBillingData.useAsaCodes = true;
                                        handleItemsTypeChange();
                                    }}
                                    disabled={!isInsuranceTypeEnableSwitchItemType}
                                />
                                <p>ASA</p>
                            </div>
                        </div>
                    {/if}
                </div>

                <!-- <div class="flex gap-x-4">
                    <p class="w-48">Within Fund Rules:</p>
                    <select
                        class="input input-bordered input-sm w-60"
                        bind:value={modalBillingData.withinFundRules}
                    >
                        <option value={true}>True</option>
                        <option value={false}>False</option>
                    </select>
                </div> -->

                {#if !modalBillingData.id || type
                        .toString()
                        .toLowerCase() == "quote" || (type
                        .toString()
                        .toLowerCase() == "invoice" && patientEncounterDetails.typeOfBill != 0)}
                    <div class="flex flex-col flex-1 justify-end gap-y-2">
                        <div class="flex gap-x-4 items-center">
                            <p class="w-48">Start time:</p>
                            <DateInput
                                id="startDateInput"
                                showTime
                                wrapperClass="input input-bordered input-sm w-60 px-0"
                                wrapperClassFocus="inputFocus"
                                bind:value={modalBillingData.startDate}
                                max={modalBillingData.endDate
                                    ? modalBillingData.endDate
                                    : ""}
                            />
                        </div>

                        {#if invalidDateRange}
                            <div class="flex gap-x-4">
                                <p class="text-error">Invalid date range</p>
                            </div>
                        {/if}
                    </div>
                {/if}
            </div>

            <div class="divider divider-horizontal" />

            <div class="flex flex-1 justify-start flex-col gap-y-4">
                {#if insuranceAllowManuallyChangeBillingRule}
                    <div class="flex gap-x-4">
                        <p class="w-48">Billing Type:</p>
                        <select
                            class="input input-bordered input-sm w-60"
                            bind:value={modalBillingData.billingType}
                            on:change={handleChangeBillingType}
                        >
                            {#each displayBillingTypes as type}
                                <option value={type.id}>{type.type}</option>
                            {/each}
                        </select>
                    </div>
                {/if}

                <div class="flex gap-x-4">
                    <p class="w-48">Add GST:</p>
                    <input
                        type="checkbox"
                        class="toggle toggle-secondary"
                        bind:checked={modalBillingData.addGST}
                        disabled
                    />
                </div>

                {#if modalBillingData.billingType == 0}
                    <!-- None -->
                {:else if insuranceAllowManuallyChangeBillingRule}
                    {#if modalBillingData.billingType && modalBillingData.billingType != 6}
                        <div class="flex gap-x-4">
                            <p class="w-48">
                                {modalBillingData.billingType == 1 ||
                                modalBillingData.billingType == 3
                                    ? "Actual Gap:"
                                    : modalBillingData.billingType == 2
                                      ? isWcOrTacUsingAmaRates
                                          ? "AMA Rates"
                                          : "Unit Value:"
                                      : modalBillingData.billingType == 4
                                        ? "Hourly Rate:"
                                        : modalBillingData.billingType == 5
                                          ? "Total Fee:"
                                          : ""}
                            </p>
                            <DecimalInput
                                inputClass="w-60"
                                bind:value={modalBillingData.calculationAmount}
                                isDisabled={modalBillingData.billingType == 1}
                            />
                        </div>
                    {:else}
                        <!-- Set MBS Fee Percentage -->
                    {/if}
                {:else}
                    <!-- Hide manually input fields -->
                {/if}

                <div class="flex gap-x-4">
                    <p class="w-48">Rebate:</p>
                    <p class="w-60 pl-4">
                        {getAmountInCurrency(modalBillingData.rebate ?? 0)}
                    </p>
                </div>

                <div class="flex gap-x-4">
                    <p class="w-48">Total Fee:</p>

                    <p class="w-60 pl-4">{displayTotalFee}</p>
                </div>

                {#if !modalBillingData.id || type
                        .toString()
                        .toLowerCase() == "quote" || (type
                        .toString()
                        .toLowerCase() == "invoice" && patientEncounterDetails.typeOfBill != 0)}
                    <div class="flex flex-col flex-1 justify-end gap-y-2">
                        <div class="flex gap-x-4 items-center">
                            <p class="w-48">End time:</p>
                            <DateInput
                                id="endDateInput"
                                showTime
                                wrapperClass="input input-bordered input-sm w-60 px-0"
                                wrapperClassFocus="inputFocus"
                                bind:value={modalBillingData.endDate}
                                min={modalBillingData.startDate
                                    ? modalBillingData.startDate
                                    : ""}
                            />
                        </div>
                    </div>
                {/if}
            </div>
        </div>

        <!-- <div class="divider" />

        <div class="flex flex-1 flex-col gap-y-4">
            <div class="flex flex-1 gap-x-4">
                <div class="flex flex-1 flex-col gap-y-4">
                    <div class="flex gap-x-4">
                        <p class="w-48">Insurance type:</p>

                        <select
                            class="select select-bordered select-sm w-60"
                            bind:value={insuranceObj.insuranceType}
                            on:change={handleChangeBillingType}
                        >
                            {#each InsuranceType.filter((it) => it.id != 25 && it.id != 26) as insurance}
                                <option
                                    value={insurance.id}
                                    selected={insurance.id ==
                                        insuranceObj.insuranceType}
                                >
                                    {insurance.type}
                                </option>
                            {/each}
                            {#if insuranceObj.insuranceType == unknownPatientInsuranceType}
                                <option
                                    value={unknownPatientInsuranceType}
                                    selected
                                    disabled
                                >
                                    Unknown
                                </option>
                            {:else if insuranceObj.insuranceType == 25}
                                <option value={Number(25)} selected>
                                    Australian Defence Force
                                </option>
                            {:else if insuranceObj.insuranceType == 26}
                                <option value={Number(26)} selected>
                                    Australian Defence Force
                                </option>
                            {:else}
                                <option value={Number(25)}>
                                    Australian Defence Force
                                </option>
                            {/if}
                        </select>
                    </div>

                    {#if insuranceObj.insuranceType != 10 && insuranceObj.insuranceType != unknownPatientInsuranceType}
                        <div class="flex gap-x-4">
                            <p class="w-48">Medicare No.:</p>

                            <input
                                type="text"
                                id="medicareNoField"
                                class="input input-bordered input-sm w-60"
                                autocomplete="off"
                                bind:value={insuranceObj.medicareNo}
                                on:focus={_resetErrorStyle("medicareNoField")}
                                on:input={({ target }) => {
                                    insuranceObj.medicareNo = target.value
                                        .replaceAll(" ", "")
                                        .replaceAll("-", "");
                                }}
                            />
                        </div>

                        <div class="flex gap-x-4">
                            <p class="w-48">Medicare Ref No.:</p>

                            <input
                                type="text"
                                id="medicareRefNoField"
                                class="input input-bordered input-sm w-60"
                                autocomplete="off"
                                bind:value={insuranceObj.medicareRefNo}
                                on:focus={_resetErrorStyle(
                                    "medicareRefNoField",
                                )}
                            />
                        </div>
                    {/if}
                </div>

                <div class="divider divider-horizontal" />

                <div class="flex flex-1 flex-col gap-y-4">
                    {#if insuranceObj.insuranceType == 16 || insuranceObj.insuranceType == 17}
                        <div class="flex gap-x-4">
                            <p class="w-48">State:</p>

                            <select
                                class="select select-bordered select-sm"
                                bind:value={modalBillingData.patientInsuranceState}
                            >
                                {#each AustralianStates as state}
                                    <option value={state.key}
                                        >{state.name}</option
                                    >
                                {/each}
                            </select>
                        </div>
                    {/if}

                    {#if insuranceObj.insuranceType == 0}
                        <div class="flex gap-x-4">
                            <p class="w-48">Insurance Fund:</p>

                            {#if isLoadingInsuranceFund}
                                <div class="flex gap-x-2">
                                    <Loader />
                                    <p>Getting options</p>
                                </div>
                            {:else if !$insuranceFund.length}
                                <p class="w-60">No Insurance Fund found</p>
                            {:else}
                                <div class="w-60" id="insuranceFundContainer">
                                    <Select
                                        bind:value={insuranceObj.insuranceFund}
                                        class="flex flex-1 max-h-[35px]"
                                        items={$insuranceFund}
                                        getOptionLabel={_getInsuranceFundLabel}
                                        getSelectionLabel={_getInsuranceFundLabel}
                                        on:clear={_clearInsuranceFund}
                                        on:focus={_clearInsuranceFund}
                                        on:select={_handleSelectInsuranceFund}
                                        noOptionsMessage="No Insurance found"
                                        placeholder="Insurance fund"
                                    />
                                </div>
                            {/if}
                        </div>

                        {#if showCoverTypeForPrivateInsurance}
                            <div class="flex gap-x-4">
                                <p class="w-48">Cover Type:</p>

                                {#if isAmaInsurance}
                                    <select
                                        class="select select-bordered select-sm w-60"
                                        bind:value={insuranceObj.privateCoverType}
                                    >
                                        <option
                                            value="Normal Cover"
                                            selected={insuranceObj.privateCoverType ==
                                                "Normal Cover" ||
                                                !insuranceObj.privateCoverType}
                                        >
                                            Prime Choice Gold (AHSA RVGs)
                                        </option>
                                        <option
                                            value="Top Cover"
                                            selected={insuranceObj.privateCoverType ==
                                                "Top Cover"}
                                        >
                                            Top Cover Gold (AMA RVGs)
                                        </option>
                                    </select>
                                {:else if isAuhInsurance}
                                    <select
                                        class="select select-bordered select-sm flex flex-1"
                                        bind:value={insuranceObj.privateCoverType}
                                    >
                                        <option
                                            value="Basic Plus"
                                            selected={insuranceObj.privateCoverType ==
                                                "Basic Plus" ||
                                                !insuranceObj.privateCoverType}
                                        >
                                            Basic Plus
                                        </option>
                                        <option
                                            value="Core/Standard"
                                            selected={insuranceObj.privateCoverType ==
                                                "Core/Standard"}
                                        >
                                            Core/Standard
                                        </option>
                                    </select>
                                {/if}
                            </div>
                        {/if}

                        <div class="flex gap-x-4">
                            <p class="w-48">Membership No.:</p>

                            <input
                                type="text"
                                id="membershipNoInput"
                                class="input input-bordered input-sm w-60"
                                autocomplete="off"
                                bind:value={insuranceObj.membershipNo}
                                on:focus={() =>
                                    _resetErrorStyle("membershipNoInput")}
                            />
                        </div>

                        <div class="flex gap-x-4">
                            <p class="w-48">Membership Ref No.:</p>

                            <input
                                type="text"
                                id="membershipRefNoInput"
                                class="input input-bordered input-sm w-60"
                                autocomplete="off"
                                bind:value={insuranceObj.membershipRefNo}
                                on:focus={() =>
                                    _resetErrorStyle("membershipRefNoInput")}
                            />
                        </div>
                    {:else if insuranceObj.insuranceType == 20}
                        <div class="flex gap-x-4">
                            <p class="w-48">Veteran No.:</p>

                            <input
                                type="text"
                                id="insuranceDetailsDVAField"
                                class="input input-bordered input-sm w-60"
                                autocomplete="off"
                                bind:value={insuranceObj.veteranNo}
                                on:focus={() =>
                                    _resetErrorStyle(
                                        "insuranceDetailsDVAField",
                                    )}
                            />
                        </div>
                    {:else if insuranceObj.insuranceType == 25 || insuranceObj.insuranceType == 26}
                        <div class="flex gap-x-4">
                            <p class="w-48">PID:</p>

                            <input
                                type="text"
                                id="insuranceDetailsPIDField"
                                class="input input-bordered input-sm w-60"
                                autocomplete="off"
                                bind:value={insuranceObj.pidNumber}
                                on:focus={() =>
                                    _resetErrorStyle(
                                        "insuranceDetailsPIDField",
                                    )}
                            />
                        </div>

                        <div class="flex gap-x-4">
                            <p class="w-48">DAN:</p>

                            <input
                                type="text"
                                id="insuranceDetailsDANField"
                                class="input input-bordered input-sm w-60"
                                autocomplete="off"
                                bind:value={insuranceObj.danNumber}
                                on:focus={() =>
                                    _resetErrorStyle(
                                        "insuranceDetailsDANField",
                                    )}
                            />
                        </div>
                    {:else if insuranceObj.insuranceType == 30}
                        <div class="flex gap-x-4">
                            <p class="w-48">Contact Email:</p>

                            <input
                                type="text"
                                id="pipEmailInput"
                                class="input input-bordered input-sm w-60"
                                autocomplete="off"
                                bind:value={insuranceObj.pipEmail}
                                on:focus={() =>
                                    _resetErrorStyle("pipEmailInput")}
                            />
                        </div>

                        {#if !isValidPipEmail}
                            <p class="text-error">Invalid Email</p>
                        {/if}

                        <div class="flex gap-x-4">
                            <p class="w-48">Vendor No.:</p>

                            <input
                                type="text"
                                id="pipVendorNoInput"
                                class="input input-bordered input-sm w-60"
                                placeholder="(if applicable)"
                                autocomplete="off"
                                bind:value={insuranceObj.vendorNumbers}
                                on:focus={() =>
                                    _resetErrorStyle("pipVendorNoInput")}
                            />
                        </div>
                    {/if}
                </div>
            </div>

            {#if insuranceObj.insuranceType == 10}
                <div class="flex flex-1 gap-x-4">
                    <p class="w-48">Overseas Cover:</p>

                    <div class="flex flex-1">
                        <input
                            type="text"
                            id="overseasCoverField"
                            class="input input-bordered input-sm w-full"
                            autocomplete="off"
                            bind:value={insuranceObj.overseasCover}
                            on:focus={() =>
                                _resetErrorStyle("overseasCoverField")}
                        />
                    </div>
                </div>
            {:else if insuranceObj.insuranceType == 16}
                <div
                    class="flex flex-1 gap-x-4 p-2 border-2 rounded-md justify-center"
                >
                    <div class="flex flex-col gap-y-2">
                        <div class="flex gap-x-4 items-center">
                            <p class="w-48">Worker insurer:</p>

                            {#if isLoadingWorkersCompensation}
                                <div class="flex gap-x-2">
                                    <Loader />
                                    <p>Getting options</p>
                                </div>
                            {:else if !$workersCompensation.length}
                                <p class="w-60">No Worker Insurer Found</p>
                            {:else}
                                <div
                                    class="w-60"
                                    id="workerCompensationContainer"
                                >
                                    <Select
                                        bind:value={insuranceObj.workerCompensationObj}
                                        class="flex flex-1 max-h-[35px] rounded-full"
                                        items={$workersCompensation}
                                        getOptionLabel={_getWorkerCompensationLabel}
                                        getSelectionLabel={_getWorkerCompensationLabel}
                                        on:clear={_clearWorkerCompensation}
                                        on:focus={_clearWorkerCompensation}
                                        on:select={_updateWorkcoverInsurerEmail}
                                        noOptionsMessage="No compensation found"
                                        placeholder="Workcover insurer (if known)"
                                    />
                                </div>
                            {/if}
                        </div>

                        {#if insuranceObj.workerCompensationObj?.id}
                            <div class="flex gap-x-4">
                                <p class="w-48">Insurer Email:</p>

                                <input
                                    type="text"
                                    id="workerCompensationInsurerEmailInput"
                                    class="input input-bordered input-sm w-60"
                                    autocomplete="off"
                                    bind:value={insuranceObj.insurerEmail}
                                    on:focus={() =>
                                        _resetErrorStyle(
                                            "workerCompensationInsurerEmailInput",
                                        )}
                                />
                            </div>

                            {#if !isValidInsurerEmail}
                                <p class="text-error">Invalid Email</p>
                            {/if}
                        {/if}

                        <div class="flex gap-x-4">
                            <p class="w-48">Claim No.:</p>

                            <input
                                type="text"
                                class="input input-bordered input-sm w-60"
                                id="workerCompensationClaimNumberInput"
                                autocomplete="off"
                                bind:value={insuranceObj.workerClaimNumber}
                                on:focus={() =>
                                    _resetErrorStyle(
                                        "workerCompensationClaimNumberInput",
                                    )}
                            />
                        </div>
                    </div>

                    <div class="divider divider-horizontal">OR</div>

                    <div class="flex flex-col gap-y-2">
                        <div class="flex gap-x-4">
                            <p class="w-48">Employer:</p>

                            <input
                                type="text"
                                id="workerCompensationEmployerInput"
                                class="input input-bordered input-sm w-60"
                                autocomplete="off"
                                bind:value={insuranceObj.workerEmployer}
                                on:focus={() =>
                                    _resetErrorStyle(
                                        "workerCompensationEmployerInput",
                                    )}
                            />
                        </div>

                        <div class="flex gap-x-4">
                            <p class="w-48">Employer Email:</p>

                            <input
                                type="text"
                                id="workerCompensationEmployerEmailInput"
                                class="input input-bordered input-sm w-60"
                                autocomplete="off"
                                bind:value={insuranceObj.workerEmployerEmail}
                                on:focus={() =>
                                    _resetErrorStyle(
                                        "workerCompensationEmployerEmailInput",
                                    )}
                            />
                        </div>

                        {#if !isValidEmployerEmail}
                            <p class="text-error">Invalid Email</p>
                        {/if}
                    </div>
                </div>
            {:else if insuranceObj.insuranceType == 17}
                <div
                    class="flex flex-1 gap-x-4 p-2 border-2 rounded-md justify-center"
                >
                    <div class="flex flex-col gap-y-2">
                        <div class="flex gap-x-4 items-center">
                            <p class="w-48">Traffic Authority:</p>

                            {#if isLoadingTrafficAuthorities}
                                <div class="flex gap-x-2">
                                    <Loader />
                                    <p>Getting options</p>
                                </div>
                            {:else if !$trafficAccidentAuthorities.length}
                                <p class="w-60">No Traffic Authorities Found</p>
                            {:else}
                                <div
                                    class="w-60"
                                    id="trafficAuthoritiesContainer"
                                >
                                    <Select
                                        bind:value={insuranceObj.trafficAuthorityObj}
                                        class="flex flex-1 max-h-[35px] rounded-full"
                                        items={$trafficAccidentAuthorities}
                                        getOptionLabel={_getTrafficAuthorityLabel}
                                        getSelectionLabel={_getTrafficAuthorityLabel}
                                        on:clear={_clearTrafficAuthority}
                                        on:focus={_clearTrafficAuthority}
                                        on:select={_updateTrafficAuthorityEmail}
                                        noOptionsMessage="No authorities found"
                                        placeholder="Traffic Accident Authority"
                                    />
                                </div>
                            {/if}
                        </div>

                        {#if insuranceObj.trafficAuthorityObj?.id}
                            <div class="flex gap-x-4">
                                <p class="w-48">Contact Email:</p>

                                <input
                                    type="text"
                                    id="trafficAuthorityEmailInput"
                                    class="input input-bordered input-sm w-60"
                                    autocomplete="off"
                                    bind:value={insuranceObj.trafficAuthorityEmail}
                                    on:focus={() =>
                                        _resetErrorStyle(
                                            "trafficAuthorityEmailInput",
                                        )}
                                />
                            </div>

                            {#if !isValidTrafficAuthorityEmail}
                                <p class="text-error">Invalid Email</p>
                            {/if}
                        {/if}
                    </div>

                    <div class="divider divider-horizontal" />

                    <div class="flex flex-col gap-y-2">
                        <div class="flex gap-x-4">
                            <p class="w-48">Claim No.:</p>

                            <input
                                type="text"
                                class="input input-bordered input-sm w-60"
                                id="trafficClaimNoField"
                                autocomplete="off"
                                bind:value={insuranceObj.trafficClaimNumber}
                                on:focus={() =>
                                    _resetErrorStyle("trafficClaimNoField")}
                            />
                        </div>
                    </div>
                </div>
            {/if}
        </div> -->
    </div>
</NotificationModal>

<NotificationModal
    title="You are about to change Item type"
    cancelText="Cancel"
    submitText={isUpdatingBillingDetails ? "Updating" : "Confirm"}
    isModalOpen={changeItemTypeModalOpen}
    closeModal={() => closeChangeItemTypeModal()}
    confirmAction={changeItemType}
    isConfirming={isUpdatingBillingDetails}
    closeModalFunction={closeChangeItemTypeModal}
>
    <p class="whitespace-pre-line">
        Change items type to {modalBillingData.useAsaCodes ? "ASA" : "MBS"}?
    </p>
    <p class="whitespace-pre-line">
        Confirming will change the items type and remove all the existing
        billing data items.
    </p>
</NotificationModal>

<NotificationModal
    title="AMA codes are not allowed for this insurance type."
    cancelText="Close"
    submitText="Confirm"
    isModalOpen={isShowInsuranceItemConflictModal}
    closeModalFunction={closeInsuranceItemConflictModal}
    closeModal={() => closeInsuranceItemConflictModal()}
    confirmAction={() => closeInsuranceItemConflictModal(true)}
>
    <p class="whitespace-pre-line">Change items type?</p>
    <p>
        Confirming will change the items type and remove all the existing
        billing data items.
    </p>
</NotificationModal>
